// @tennis-matches/services/governance
// Rollen-basierte Viewer-Modi (CoachDesk / TrainingsDesk).

const STAFF_ROLES = new Set([
  "trainer",
  "trainer_coordinator",
  "jugendwart",
  "kassenwart",
  "owner",
  "admin",
  "vereinsadmin",
  "vorstand",
  "sportwart",
  "team_captain",
]);

const FAMILY_ROLES = new Set(["parent", "player"]);

function __asStr(x) {
  return String(x ?? "").trim().toLowerCase();
}

/**
 * Aktive Tenant-Rollen aus role_bindings sammeln.
 * @param {unknown[]} roleBindings
 * @returns {Set<string>}
 */
export function collectActiveTenantRolesFromBindings(roleBindings) {
  const roles = new Set();
  for (const b of Array.isArray(roleBindings) ? roleBindings : []) {
    if (__asStr(b?.scope) !== "tenant") continue;
    if (b?.is_active === false) continue;
    const role = __asStr(b?.role);
    if (role) roles.add(role);
  }
  return roles;
}

/**
 * Eltern- oder Spieler-Rolle für Profil/Familie — unabhängig von Staff-Rollen.
 * @param {unknown[]} roleBindings
 * @returns {'parent'|'player'|null}
 */
export function resolveFamilyProfilePortalRole(roleBindings) {
  const roles = collectActiveTenantRolesFromBindings(roleBindings);
  if (roles.has("parent")) return "parent";
  if (roles.has("player")) return "player";
  return null;
}

/**
 * Eltern- oder Spieler-Portal (ohne Staff): `parent` hat Vorrang vor `player`.
 * @param {unknown[]} roleBindings
 * @returns {'parent'|'player'|null}
 */
export function resolveFamilyPortalRole(roleBindings) {
  const roles = collectActiveTenantRolesFromBindings(roleBindings);
  if (roles.size === 0) return null;
  const hasStaff = [...roles].some((r) => STAFF_ROLES.has(r));
  if (hasStaff) return null;
  return resolveFamilyProfilePortalRole(roleBindings);
}

/**
 * Nur Eltern/Spieler ohne Staff-Rollen (TrainingsDesk Familien-Portal).
 * @param {unknown[]} roleBindings
 */
export function isFamilyOnlyRoleBindings(roleBindings) {
  return resolveFamilyPortalRole(roleBindings) != null;
}

/**
 * Nur Mannschaftsführer ohne weitere Staff-Rollen (TeamDesk statt TrainingsDesk).
 * @param {unknown[]} roleBindings
 */
export function isTeamCaptainOnlyRoleBindings(roleBindings) {
  const roles = collectActiveTenantRolesFromBindings(roleBindings);
  if (!roles.has("team_captain")) return false;
  for (const role of roles) {
    if (role !== "team_captain" && STAFF_ROLES.has(role)) return false;
  }
  return true;
}

/** Marketplace-Apps für reine Eltern/Spieler-Logins (ohne Staff-Rollen). */
export const FAMILY_ONLY_VISIBLE_APP_KEYS = Object.freeze(["trainerstunden", "matchmixer"]);

/**
 * Eltern/Spieler ohne Staff: TrainingsDesk + MatchMixer (Turniere planen & mitspielen).
 * @param {Set<string>|string[]|null|undefined} visibleAppKeys
 * @param {unknown[]} roleBindings
 * @returns {Set<string>}
 */
export function restrictEffectiveVisibleAppsForFamilyOnly(visibleAppKeys, roleBindings) {
  if (!isFamilyOnlyRoleBindings(roleBindings)) {
    if (visibleAppKeys instanceof Set) return visibleAppKeys;
    return new Set(
      (Array.isArray(visibleAppKeys) ? visibleAppKeys : [])
        .map((k) => __asStr(k))
        .filter(Boolean)
    );
  }

  const src =
    visibleAppKeys instanceof Set
      ? visibleAppKeys
      : new Set(
          (Array.isArray(visibleAppKeys) ? visibleAppKeys : [])
            .map((k) => __asStr(k))
            .filter(Boolean)
        );

  const out = new Set();
  for (const allowed of FAMILY_ONLY_VISIBLE_APP_KEYS) {
    if (src.has(allowed)) out.add(allowed);
  }
  // Produktregel: Schleifchenturniere planen — auch ohne role_app_access-Eintrag.
  out.add("matchmixer");
  return out;
}

export { STAFF_ROLES, FAMILY_ROLES };
